import { get, writable } from 'svelte/store';
import type { Capabilities, Conversation, Message, PeerInfo, ServerEvent } from '../shared/types';
import { loadIdentity, saveIdentity, type Identity } from './identity';
import { isUnreadIncoming } from './ownership';

export function sortConversations(list: Conversation[]): Conversation[] {
  return [...list].sort((a, b) => {
    if (Boolean(a.pinned) !== Boolean(b.pinned)) return a.pinned ? -1 : 1;
    return b.lastMessageAt - a.lastMessageAt;
  });
}

export function mergeMessage(list: Message[], message: Message): Message[] {
  const index = list.findIndex((item) => item.id === message.id);
  if (index === -1) {
    return [...list, message].sort((a, b) => a.ts - b.ts);
  }
  const next = [...list];
  next[index] = { ...next[index], ...message };
  return next;
}

export interface ChatData {
  connected: boolean;
  me: Identity;
  capabilities: Capabilities | null;
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  peers: PeerInfo[];
  activeId: string | null;
  settingsOpen: boolean;
  mobileChat: boolean;
  toast: { text: string; kind: 'info' | 'error' } | null;
}

const initial: ChatData = {
  connected: false,
  me: loadIdentity(),
  capabilities: null,
  conversations: [],
  messages: {},
  peers: [],
  activeId: null,
  settingsOpen: false,
  mobileChat: false,
  toast: null,
};

const store = writable<ChatData>(initial);

const update = (mutate: (state: ChatData) => Partial<ChatData>) =>
  store.update((state) => ({ ...state, ...mutate(state) }));

const read = () => get(store);

export const chat = {
  subscribe: store.subscribe,

  getState(): ChatData {
    return read();
  },

  setState(partial: Partial<ChatData>) {
    update(() => partial);
  },

  setConnected(connected: boolean) {
    update(() => ({ connected }));
  },

  setMeName(name: string) {
    const state = read();
    const me = { ...state.me, name: name.trim() || state.me.name, named: true };
    saveIdentity(me);
    update(() => ({ me }));
  },

  setCapabilities(capabilities: Capabilities) {
    update(() => ({ capabilities }));
  },

  setConversations(conversations: Conversation[]) {
    update(() => ({ conversations: sortConversations(conversations) }));
  },

  setActive(activeId: string | null) {
    update(() => ({ activeId, mobileChat: Boolean(activeId) }));
  },

  setMessages(conversationId: string, messages: Message[]) {
    update((state) => ({
      messages: { ...state.messages, [conversationId]: [...messages].sort((a, b) => a.ts - b.ts) },
    }));
  },

  upsertConversation(conversation: Conversation) {
    update((state) => {
      const others = state.conversations.filter((item) => item.id !== conversation.id);
      return { conversations: sortConversations([...others, conversation]) };
    });
  },

  removeConversation(id: string) {
    update((state) => ({
      conversations: state.conversations.filter((item) => item.id !== id),
      activeId: state.activeId === id ? null : state.activeId,
      messages: Object.fromEntries(Object.entries(state.messages).filter(([key]) => key !== id)),
    }));
  },

  upsertMessage(message: Message) {
    update((state) => {
      const list = state.messages[message.conversationId] ?? [];
      const known = list.some((item) => item.id === message.id);
      return {
        messages: { ...state.messages, [message.conversationId]: mergeMessage(list, message) },
        conversations: sortConversations(
          state.conversations.map((item) =>
            item.id === message.conversationId
              ? {
                  ...item,
                  preview: message.text.slice(0, 120) || item.preview,
                  lastMessageAt: Math.max(item.lastMessageAt, message.ts),
                  unread: isUnreadIncoming(message, state.me.id, {
                    meshPeerId: state.capabilities?.mesh.peerId,
                    activeId: state.activeId,
                    known,
                  })
                    ? (item.unread ?? 0) + 1
                    : item.unread ?? 0,
                }
              : item,
          ),
        ),
      };
    });
  },

  setPeers(peers: PeerInfo[]) {
    update(() => ({ peers }));
  },

  toggleSettings(open?: boolean) {
    update((state) => ({ settingsOpen: open ?? !state.settingsOpen }));
  },

  markRead(conversationId: string) {
    update((state) => ({
      conversations: state.conversations.map((item) => (item.id === conversationId ? { ...item, unread: 0 } : item)),
    }));
  },

  notify(text: string, kind: 'info' | 'error' = 'info') {
    update(() => ({ toast: { text, kind } }));
    setTimeout(() => {
      if (read().toast?.text === text) update(() => ({ toast: null }));
    }, 4200);
  },

  applyEvent(event: ServerEvent) {
    switch (event.type) {
      case 'hello':
        chat.setCapabilities(event.capabilities);
        chat.setConversations(event.conversations);
        chat.setPeers(event.peers);
        break;
      case 'capabilities':
        chat.setCapabilities(event.capabilities);
        break;
      case 'conversation':
        chat.upsertConversation(event.conversation);
        break;
      case 'conversation.remove':
        chat.removeConversation(event.conversationId);
        break;
      case 'message':
      case 'message.update':
        chat.upsertMessage(event.message);
        break;
      case 'presence':
        chat.setPeers(event.peers);
        break;
      case 'read':
        chat.markRead(event.conversationId);
        break;
      case 'typing':
        break;
      case 'error':
        chat.notify(event.message, 'error');
        break;
    }
  },
};
